import { useEffect, useState } from 'react';
import { IdCard, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export type ComposerRecipient = {
  id: string;
  name: string;
  phone: string;
  selected: boolean;
  source: 'auto' | 'manual';
  linkedUserId?: number | null;
  participantId?: number;
  participantType?: 'comprador' | 'vendedor';
  tramiteId?: number;
  linkedProfile?: {
    dni?: string | null;
    telefono?: string | null;
    direccion?: string | null;
  };
  isPersistable?: boolean;
};

type NotificationComposerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  recipients: ComposerRecipient[];
  onRecipientsChange: (recipients: ComposerRecipient[]) => void;
  message: string;
  onMessageChange: (message: string) => void;
  previewMessage: string;
  previewError?: string;
  onPreview: () => Promise<void> | void;
  onSend: () => Promise<void> | void;
  sending?: boolean;
  previewing?: boolean;
  loading?: boolean;
  modeLabel?: string;
  phoneErrors?: Record<string, string>;
  onEditRecipientProfile?: (recipient: ComposerRecipient) => void;
  canEditRecipientProfile?: (recipient: ComposerRecipient) => boolean;
};

const NotificationComposerModal = ({
  open,
  onOpenChange,
  title,
  description,
  recipients,
  onRecipientsChange,
  message,
  onMessageChange,
  previewMessage,
  previewError,
  onPreview,
  onSend,
  sending = false,
  previewing = false,
  loading = false,
  modeLabel,
  phoneErrors = {},
  onEditRecipientProfile,
  canEditRecipientProfile,
}: NotificationComposerModalProps) => {
  const [activeTab, setActiveTab] = useState<'message' | 'preview'>('message');

  useEffect(() => {
    if (open) {
      setActiveTab('message');
    }
  }, [open]);

  const updateRecipient = (id: string, patch: Partial<ComposerRecipient>) => {
    onRecipientsChange(recipients.map((recipient) => (recipient.id === id ? { ...recipient, ...patch } : recipient)));
  };

  const removeRecipient = (id: string) => {
    onRecipientsChange(recipients.filter((recipient) => recipient.id !== id));
  };

  const addManualRecipient = () => {
    onRecipientsChange([
      ...recipients,
      {
        id: `manual-${Date.now()}`,
        name: 'Manual',
        phone: '',
        selected: true,
        source: 'manual',
      },
    ]);
  };

  const selectedRecipients = recipients.filter((recipient) => recipient.selected);
  const hasMessage = message.trim().length > 0;

  const handleTabChange = (nextTab: string) => {
    const normalized = nextTab === 'preview' ? 'preview' : 'message';
    setActiveTab(normalized);

    if (normalized === 'preview' && hasMessage) {
      void onPreview();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[100dvh] w-[100vw] max-w-none rounded-none p-0 gap-0 overflow-hidden flex flex-col sm:h-auto sm:w-[95vw] sm:max-w-4xl sm:max-h-[90dvh] sm:rounded-lg">
        <DialogHeader className="shrink-0 px-4 sm:px-5 py-3 bg-gray-100 border-b">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
          <div className="border-b pb-3 space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <label className="text-sm text-gray-600">Destinatarios</label>
              <Button type="button" variant="outline" size="sm" onClick={addManualRecipient} className="w-full sm:w-auto">
                <Plus size={14} className="mr-1" />
                Agregar manual
              </Button>
            </div>

            {recipients.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin destinatarios autodescubiertos. Puedes agregar destinatarios manualmente.</p>
            ) : (
              <div className="space-y-2 max-h-56 sm:max-h-64 overflow-y-auto pr-1">
                {recipients.map((recipient) => (
                  <div key={recipient.id} className="space-y-1">
                    <div className={`rounded-xl border px-3 py-2 flex flex-col gap-2 bg-gray-50 lg:flex-row lg:items-center ${phoneErrors[recipient.id] ? 'border-red-300' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-2 lg:shrink-0">
                        <input
                          type="checkbox"
                          checked={recipient.selected}
                          onChange={(e) => updateRecipient(recipient.id, { selected: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <Badge variant={recipient.source === 'auto' ? 'secondary' : 'outline'}>
                          {recipient.source === 'auto' ? 'Auto' : 'Manual'}
                        </Badge>
                      </div>
                      <Input value={recipient.name} onChange={(e) => updateRecipient(recipient.id, { name: e.target.value })} className="h-8 w-full min-w-0 lg:w-[320px] lg:flex-none lg:max-w-[40%]" placeholder="Nombre" />
                      <Input
                        value={recipient.phone}
                        onChange={(e) => updateRecipient(recipient.id, { phone: e.target.value })}
                        className={`h-8 w-full min-w-0 lg:min-w-[12rem] lg:flex-1 ${phoneErrors[recipient.id] ? 'border-red-400 focus:border-red-500' : ''}`}
                        placeholder="Teléfono"
                        aria-invalid={!!phoneErrors[recipient.id]}
                      />
                      {onEditRecipientProfile && canEditRecipientProfile?.(recipient) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onEditRecipientProfile(recipient)}
                              className="rounded-full w-8 h-8 p-0"
                              aria-label="Editar datos"
                            >
                              <IdCard className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Editar datos personales</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeRecipient(recipient.id)}
                            className="rounded-full w-8 h-8 p-0 text-red-500 border-red-300 hover:bg-red-50 focus:ring-red-500"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Eliminar destinatario</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {phoneErrors[recipient.id] && (
                      <p className="text-xs text-red-600 pl-3">{phoneErrors[recipient.id]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedRecipients.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedRecipients.map((recipient) => (
                  <Badge key={`selected-${recipient.id}`} variant="outline" className="max-w-full">
                    <span className="truncate">
                      {recipient.name || 'Sin nombre'}: {recipient.phone || 'sin teléfono'}
                    </span>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-md border p-3 min-h-0">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <p className="text-sm text-gray-600">Mensaje y previsualización</p>
              <p className="text-xs text-muted-foreground">{modeLabel ?? ''}</p>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-3">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="message">Mensaje</TabsTrigger>
                <TabsTrigger value="preview">Previsualización</TabsTrigger>
              </TabsList>

              <TabsContent value="message">
                <Textarea value={message} onChange={(e) => onMessageChange(e.target.value)} rows={5} className="min-h-[220px] max-h-[45dvh]" placeholder="Escribe el mensaje a enviar" />
              </TabsContent>

              <TabsContent value="preview">
                <div className="border rounded-md p-3 text-sm min-h-[220px] max-h-[45dvh] overflow-y-auto bg-muted/20 whitespace-pre-wrap">
                  {!hasMessage
                    ? 'Escribe un mensaje para ver la previsualización.'
                    : previewError
                      ? previewError
                      : previewing
                        ? 'Previsualizando...'
                        : previewMessage || 'Sin previsualización aún.'}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter className="shrink-0 px-4 sm:px-5 py-4 border-t bg-gray-50">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancelar</Button>
          <Button type="button" onClick={onSend} disabled={sending || loading}>{sending ? 'Enviando...' : 'Enviar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationComposerModal;
